/**
 * PinDetailDialog — reusable map-pin detail modal for direction variants.
 *
 * Replicates the inline Dialog that DashboardPage renders for its `current`
 * branch, so CommandHome / SaasHome / ExpressiveHome can also respond to
 * map marker clicks. Receives the selected pin (null = closed) and an
 * onClose callback.
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Camera, Activity, MapPin, FolderOpen, ExternalLink, X } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import type { CameraMapPin } from '@/types'

// useRelativeTime — mirrors DashboardPage's identical hook
function useRelativeTime() {
  const { t: tc } = useTranslation('common')
  return (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const s = Math.floor(diff / 1000)
    if (s < 60) return tc('time.justNow')
    const m = Math.floor(s / 60)
    if (m < 60) return tc('time.minutesAgo', { count: m })
    const h = Math.floor(m / 60)
    if (h < 24) return tc('time.hoursAgo', { count: h })
    const d = Math.floor(h / 24)
    return tc('time.daysAgo', { count: d })
  }
}

interface PinDetailDialogProps {
  pin: CameraMapPin | null
  onClose: () => void
}

export function PinDetailDialog({ pin, onClose }: PinDetailDialogProps) {
  const { t } = useTranslation('cameras')
  const { t: te } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const relativeTime = useRelativeTime()
  const navigate = useNavigate()

  if (!pin) return null

  const lat = parseFloat(pin.latitude)
  const lng = parseFloat(pin.longitude)
  const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`

  return (
    <Dialog.Root open={!!pin} onOpenChange={(open) => { if (!open) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Dialog.Title className="sr-only">{pin.name}</Dialog.Title>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                <Camera className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{pin.name}</p>
                {pin.location && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <p className="text-xs text-muted-foreground truncate">{pin.location}</p>
                  </div>
                )}
              </div>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Details */}
          <div className="px-5 py-4 space-y-3">
            {/* Group */}
            {pin.group_names.length > 0 && (
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <p className="text-sm text-muted-foreground">{pin.group_names.join(', ')}</p>
              </div>
            )}

            {/* Coordinates */}
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm font-mono text-muted-foreground">{lat.toFixed(6)}, {lng.toFixed(6)}</p>
            </div>

            {/* Last Event */}
            {pin.last_event_type && pin.last_event_at && (
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {(tc as any)(`eventTypes.${pin.last_event_type}`, { defaultValue: pin.last_event_type.replace(/_/g, ' ') })}
                  </p>
                  <p className="text-xs text-muted-foreground">{relativeTime(pin.last_event_at)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
            <Button
              variant="default"
              size="sm"
              className="flex-1 h-9 text-xs gap-1.5"
              onClick={() => {
                if (pin.is_locked) {
                  toast.error('Camera locked', { description: 'SIM mismatch — under review.' })
                  return
                }
                onClose(); navigate(`/events?camera=${pin.id}`)
              }}
            >
              <Activity className="h-3.5 w-3.5" />
              {te('viewAll')}
            </Button>
            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button variant="outline" size="sm" className="w-full h-9 text-xs gap-1.5">
                <ExternalLink className="h-3.5 w-3.5" />
                {t('map.openInMaps')}
              </Button>
            </a>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
